import { Component, OnInit, ChangeDetectionStrategy, Inject, Optional } from '@angular/core';

import { MSAL_GUARD_CONFIG, MsalBroadcastService, MsalGuardConfiguration, MsalService } from '@azure/msal-angular';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { EventMessage, EventType, RedirectRequest } from '@azure/msal-browser';
import { AuthDispatchers, AuthSelectors } from './store/services';
import { ActivatedRoute } from '@angular/router';
import { environment } from '../../environments/environment';

@Component({
    selector: 'app-login',
    template: `
        <app-login-ui
            [loggedIn]="loggedIn$ | async"
            [signingIn]="signingIn$ | async"
            [loginError]="loginError$ | async"
            [isCloud]="isCloud"
            (login)="login($event)"
            (logout)="logoutRedirect()"
        ></app-login-ui>
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})

export class AuthContainer implements OnInit {
    loggedIn$ = this.authSelector.loggedIn$;
    signingIn$ = this.authSelector.signingIn$;
    loginError$ = this.authSelector.loginError$;
    isCloud = environment['authMode'] === 'cloud';
    private readonly _destroying$ = new Subject<void>();

    constructor(
        @Optional() @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
        @Optional() private msalService: MsalService,
        @Optional() private msalBroadcastService: MsalBroadcastService,
        private route: ActivatedRoute,
        private authSelector: AuthSelectors,
        private authDispatch: AuthDispatchers,
    ) { }

    ngOnInit() {
        if (!this.isCloud) {
            const noAutoLogin = this.route.snapshot.queryParams['noautologin'] || '';
            if (noAutoLogin === '') {
                this.authDispatch.autoLogin();
            }
        } else {
            this.msalService.instance.enableAccountStorageEvents();
            // // OnLogin
            this.msalService.handleRedirectObservable().pipe(
                takeUntil(this._destroying$)
            ).subscribe({
                next: async (result) => {
                    const account = result?.account ?? this.msalService.instance.getAllAccounts()[0];
                    if (account) {
                        this.msalService.instance.setActiveAccount(account);
                        this.authDispatch.setLoggedInByMsal();
                    }
                },
                error: (error) => {
                    console.error('Redirect handling failed:', error);
                    this.authDispatch.loginFailed('Der opstod en fejl i login. Der er muligvis ingen forbindelse til serveren.');
                }
            });
            // OnLogout
            this.msalBroadcastService.msalSubject$.pipe(
                filter((msg: EventMessage) => msg.eventType === EventType.ACCOUNT_ADDED || msg.eventType === EventType.ACCOUNT_REMOVED),
                takeUntil(this._destroying$)
            ).subscribe((_result: EventMessage) => {
                if (this.msalService.instance.getAllAccounts().length === 0) {
                    window.location.pathname = '/';
                }
            });
            // OnLogin fejl
            this.msalBroadcastService.msalSubject$.pipe(
                filter((msg: EventMessage) => msg.eventType === EventType.LOGIN_FAILURE),
                takeUntil(this._destroying$)
            ).subscribe((result: EventMessage) => {
                console.log('login failure', result);
                this.authDispatch.loginFailed('Der opstod en fejl i login. Der er muligvis ingen forbindelse til serveren.');
            });
        }
    }

    login(value: {username: string, password: string}) {
        if (!this.isCloud) {
            this.authDispatch.login(value.username, value.password);
        } else {
            this.loginRedirect();
        }
    }

    loginRedirect() {
        this.msalService.loginRedirect({...this.msalGuardConfig.authRequest} as RedirectRequest);
    }

    logoutRedirect() {
        this.msalService.logoutRedirect();
    }

    ngOnDestroy(): void {
        this._destroying$.next(undefined);
        this._destroying$.complete();
    }
}
